// ============================================================================
// HAI Reporting Tool - Sync Service
// Outbox pattern for offline-first data synchronization
// ============================================================================

import { db as localDb } from './db';
import {
  saveDeviceSnapshot,
  saveHAIRecord,
  saveSSISurveillance
} from './firestore.service';
import type { SyncQueueItem, SyncOperation } from '@/types';

// Sync status callback type
type SyncStatusCallback = (status: {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncAt?: Date;
  error?: string;
}) => void;

class SyncService {
  private isSyncing = false;
  private syncInterval: number | null = null;
  private statusCallbacks: SyncStatusCallback[] = [];
  private readonly SYNC_INTERVAL_MS = 30000; // 30 seconds
  private readonly MAX_RETRIES = 5;

  /**
   * Initialize sync service and start background sync
   */
  async initialize(): Promise<void> {
    console.log('Initializing sync service...');

    // Check initial online status
    await this.updateOnlineStatus(navigator.onLine);

    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    // Start periodic sync if online
    if (navigator.onLine) {
      this.startPeriodicSync();
    }
  }

  /**
   * Add a callback to be notified of sync status changes
   */
  onSyncStatusChange(callback: SyncStatusCallback): () => void {
    this.statusCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      this.statusCallbacks = this.statusCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all status callbacks
   */
  private notifyStatusChange(status: Parameters<SyncStatusCallback>[0]): void {
    this.statusCallbacks.forEach(callback => callback(status));
  }

  /**
   * Handle coming online
   */
  private async handleOnline(): Promise<void> {
    console.log('Device came online');
    await this.updateOnlineStatus(true);
    this.startPeriodicSync();
    await this.syncNow(); // Immediate sync when coming online
  }

  /**
   * Handle going offline
   */
  private async handleOffline(): Promise<void> {
    console.log('Device went offline');
    await this.updateOnlineStatus(false);
    this.stopPeriodicSync();
  }

  /**
   * Update online status in local DB
   */
  private async updateOnlineStatus(isOnline: boolean): Promise<void> {
    await localDb.updateAppMetadata({ isOnline });
  }

  /**
   * Start periodic background sync
   */
  private startPeriodicSync(): void {
    if (this.syncInterval !== null) return;

    this.syncInterval = window.setInterval(() => {
      this.syncNow();
    }, this.SYNC_INTERVAL_MS);

    console.log('Started periodic sync (30s interval)');
  }

  /**
   * Stop periodic background sync
   */
  private stopPeriodicSync(): void {
    if (this.syncInterval !== null) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('Stopped periodic sync');
    }
  }

  /**
   * Add item to sync queue
   */
  async addToQueue(
    operation: SyncOperation,
    entityType: string,
    entityId: string,
    payload: any
  ): Promise<string> {
    const item: Omit<SyncQueueItem, 'id'> = {
      operation,
      entityType,
      entityId,
      payload,
      status: 'PENDING',
      retries: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const id = await localDb.addToSyncQueue(item);
    console.log(`Added to sync queue: ${entityType} ${operation}`, id);

    // Trigger immediate sync if online
    if (navigator.onLine && !this.isSyncing) {
      setTimeout(() => this.syncNow(), 1000);
    }

    return id;
  }

  /**
   * Perform sync now
   */
  async syncNow(): Promise<void> {
    if (this.isSyncing) {
      console.log('Sync already in progress, skipping');
      return;
    }

    if (!navigator.onLine) {
      console.log('Device offline, skipping sync');
      return;
    }

    this.isSyncing = true;
    await localDb.updateAppMetadata({ syncStatus: 'syncing' });

    try {
      const pendingItems = await localDb.getPendingSyncItems();

      if (pendingItems.length === 0) {
        console.log('No pending items to sync');
        await this.completeSyncSuccess();
        return;
      }

      console.log(`Syncing ${pendingItems.length} pending items...`);
      this.notifyStatusChange({
        isSyncing: true,
        pendingCount: pendingItems.length
      });

      let successCount = 0;
      let errorCount = 0;

      for (const item of pendingItems) {
        try {
          await this.syncItem(item);
          await localDb.markSyncItemComplete(item.id);
          successCount++;
        } catch (error: any) {
          console.error(`Failed to sync item ${item.id}:`, error);

          if (item.retries < this.MAX_RETRIES) {
            await localDb.markSyncItemFailed(item.id, error.message);
          } else {
            console.error(`Item ${item.id} exceeded max retries, marking as error`);
            await localDb.markSyncItemFailed(item.id, 'Max retries exceeded');
          }
          errorCount++;
        }
      }

      console.log(`Sync complete: ${successCount} success, ${errorCount} errors`);
      await this.completeSyncSuccess();

    } catch (error: any) {
      console.error('Sync failed:', error);
      await this.completeSyncError(error.message);
    }
  }

  /**
   * Sync a single item
   */
  private async syncItem(item: SyncQueueItem): Promise<void> {
    const { operation, entityType, payload } = item;

    switch (entityType) {
      case 'deviceSnapshot':
        await this.syncDeviceSnapshot(operation, payload);
        break;

      case 'haiRecord':
        await this.syncHAIRecord(operation, payload);
        break;

      case 'ssiSurveillance':
        await this.syncSSISurveillance(operation, payload);
        break;

      default:
        throw new Error(`Unknown entity type: ${entityType}`);
    }
  }

  /**
   * Sync device snapshot with conflict resolution
   */
  private async syncDeviceSnapshot(operation: SyncOperation, payload: any): Promise<void> {
    try {
      if (operation === 'CREATE') {
        await saveDeviceSnapshot(payload, false);
      } else if (operation === 'UPDATE') {
        // Last-write-wins: always use local version for updates
        await saveDeviceSnapshot(payload, true, payload.id);
      }
      // DELETE not implemented for device snapshots
    } catch (error: any) {
      // If conflict detected (document modified), retry with latest updatedAt
      if (error.code === 'permission-denied' || error.code === 'failed-precondition') {
        console.warn('Conflict detected, applying last-write-wins strategy');
        payload.updatedAt = new Date();
        await saveDeviceSnapshot(payload, true, payload.id);
      } else {
        throw error;
      }
    }
  }

  /**
   * Sync HAI record with conflict resolution
   */
  private async syncHAIRecord(operation: SyncOperation, payload: any): Promise<void> {
    try {
      if (operation === 'CREATE') {
        await saveHAIRecord(payload, false);
      } else if (operation === 'UPDATE') {
        await saveHAIRecord(payload, true, payload.id);
      }
    } catch (error: any) {
      if (error.code === 'permission-denied' || error.code === 'failed-precondition') {
        console.warn('Conflict detected for HAI record, applying last-write-wins');
        payload.updatedAt = new Date();
        await saveHAIRecord(payload, true, payload.id);
      } else {
        throw error;
      }
    }
  }

  /**
   * Sync SSI surveillance with conflict resolution
   */
  private async syncSSISurveillance(operation: SyncOperation, payload: any): Promise<void> {
    try {
      if (operation === 'CREATE') {
        await saveSSISurveillance(payload, false);
      } else if (operation === 'UPDATE') {
        await saveSSISurveillance(payload, true, payload.id);
      }
    } catch (error: any) {
      if (error.code === 'permission-denied' || error.code === 'failed-precondition') {
        console.warn('Conflict detected for SSI surveillance, applying last-write-wins');
        payload.updatedAt = new Date();
        await saveSSISurveillance(payload, true, payload.id);
      } else {
        throw error;
      }
    }
  }

  /**
   * Complete sync successfully
   */
  private async completeSyncSuccess(): Promise<void> {
    this.isSyncing = false;
    await localDb.updateAppMetadata({
      syncStatus: 'idle',
      lastSyncAt: new Date(),
      pendingSyncCount: 0
    });

    this.notifyStatusChange({
      isSyncing: false,
      pendingCount: 0,
      lastSyncAt: new Date()
    });
  }

  /**
   * Complete sync with error
   */
  private async completeSyncError(error: string): Promise<void> {
    this.isSyncing = false;
    await localDb.updateAppMetadata({ syncStatus: 'error' });

    const pendingCount = await localDb.syncQueue.where('status').equals('PENDING').count();

    this.notifyStatusChange({
      isSyncing: false,
      pendingCount,
      error
    });
  }

  /**
   * Get current sync status
   */
  async getStatus(): Promise<{
    isSyncing: boolean;
    pendingCount: number;
    isOnline: boolean;
    lastSyncAt?: Date;
  }> {
    const metadata = await localDb.getAppMetadata();
    const pendingCount = await localDb.syncQueue.where('status').equals('PENDING').count();

    return {
      isSyncing: this.isSyncing,
      pendingCount,
      isOnline: metadata.isOnline,
      lastSyncAt: metadata.lastSyncAt
    };
  }

  /**
   * Clear all failed sync items (admin function)
   */
  async clearFailedItems(): Promise<number> {
    const failedItems = await localDb.syncQueue.where('status').equals('ERROR').toArray();

    for (const item of failedItems) {
      await localDb.syncQueue.delete(item.id);
    }

    return failedItems.length;
  }
}

// Export singleton instance
export const syncService = new SyncService();
